import {inject, injectable} from 'inversify';
import {makeAutoObservable} from 'mobx';

import type {Destructible} from '../../../../utils/store';
import type {Nullable} from '../../../../utils/utility-types';
import type {DataFilterState} from '../../models/DataFilterState';
import type {DataFilterStore} from '../DataFilter/DataFilter.store';

import {not} from '../../../../utils/conditionalUtils';
import {FlagManager} from '../../../../utils/store';
import {DataFilterFieldInput} from '../../models/DataFilterFieldInput';
import {DataFilterLabelsStore} from '../DataFilterLabels/DataFilterLabels.store';

@injectable()
export class DataFilterFieldsManagerStore<Data = any> implements Destructible {
  isEnabled = new FlagManager(false);
  isOpen = new FlagManager();
  isReady = new FlagManager();
  private config: DataFilterStore.FieldsConfig<Data>;
  private store: DataFilterStore<Data> | null = null;

  constructor(
    @inject(DataFilterLabelsStore) readonly labels: DataFilterLabelsStore,
  ) {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  init(
    config: Nullable<DataFilterStore.FieldsConfig<Data>>,
    store: DataFilterStore<Data>,
  ) {
    if (config) {
      this.config = config;
      this.isEnabled.setTrue();
    }

    this.store = store;
    this.isReady.setTrue();
  }

  destruct() {}

  get activeFiltersAmount() {
    return this.store?.state.fields?.size || 0;
  }

  get isClear() {
    if (!this.store?.state.fields) {
      return true;
    }

    return this.store?.state.fields?.size === 0;
  }

  get isLoading() {
    return Boolean(this.store?.isLoading);
  }

  get renderFields() {
    const inputs = this.config.fields.map(field => {
      const {key, label, placeholder} = field;

      return new DataFilterFieldInput(
        key,
        label,
        placeholder,
        this.getOptions(field),
      );
    });

    this.store?.state.fields?.forEach(field => {
      const input = inputs.find(item => item.key === field.key);

      if (input) {
        input.setSelected([...field.values]);
      }
    });

    return inputs;
  }

  apply() {
    const fieldsEntries: [string, DataFilterState.Field][] = this.renderFields
      .filter(field => not(field.isClear))
      .map(field => [field.key, field.toState()]);

    const fields = new Map<string, DataFilterState.Field>(fieldsEntries);

    this.store?.setState(this.store.state.updateFields(fields));
  }

  clear() {
    this.store?.setState(this.store.state.updateFields(null));
  }

  filterData(data: Data[], state: DataFilterStore.State): Data[] {
    if (this.isEnabled.isFalse) {
      return data;
    }

    const isNoKeysToFilter = !this.config || this.config.fields.length === 0;

    if (!state.fields || state.fields.size === 0 || isNoKeysToFilter) {
      return data;
    }

    const result: Data[] = [];

    for (const dataEntry of data) {
      const validation: boolean[] = [];

      for (const [fieldKey, field] of state.fields) {
        const value = dataEntry[fieldKey] as string;

        validation.push(field.values.has(value));
      }

      const isPassFilterValidation =
        validation.length === state.fields.size &&
        validation.every(verdict => verdict === true);

      if (isPassFilterValidation) {
        result.push(dataEntry);
      }
    }

    return result;
  }

  private getOptions(field: DataFilterStore.FieldItemConfig<Data>): string[] {
    const options = new Set<string>();

    this.config.dataGetter().forEach(item => {
      options.add(item[field.key] as string);
    });

    return Array.from(options);
  }
}
